import type { CSSProperties, FC } from 'react';
import styled, { css, keyframes } from 'styled-components';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Pulse animation
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
`;

type SkeletonVariant = 'text' | 'circular' | 'rectangular' | 'rounded';
type SkeletonAnimation = 'shimmer' | 'pulse' | 'none';

interface SkeletonBaseProps {
	width?: string | number;
	height?: string | number;
	variant?: SkeletonVariant;
	animation?: SkeletonAnimation;
	className?: string;
	style?: CSSProperties;
}

const getWidth = (width: string | number | undefined) => {
	if (typeof width === 'number') return `${width}px`;
	return width || '100%';
};

const getHeight = (height: string | number | undefined, variant?: SkeletonVariant) => {
	if (typeof height === 'number') return `${height}px`;
	if (height) return height;
	if (variant === 'text') return '1em';
	if (variant === 'circular') return '40px';
	return '100%';
};

const SkeletonBase = styled.div<{
	$width: string;
	$height: string;
	$variant: SkeletonVariant;
	$animation: SkeletonAnimation;
}>`
	display: block;
	background: var(--bg-tertiary, #f0f0f0);
	width: ${({ $width }) => $width};
	height: ${({ $height }) => $height};

	${({ $variant }) => {
		switch ($variant) {
			case 'circular':
				return css`
					border-radius: 50%;
				`;
			case 'rounded':
				return css`
					border-radius: var(--radius-md, 6px);
				`;
			case 'text':
				return css`
					border-radius: var(--radius-sm, 3px);
					transform: scale(1, 0.8);
				`;
			default:
				return css`
					border-radius: 0;
				`;
		}
	}}

	${({ $animation }) => {
		switch ($animation) {
			case 'shimmer':
				return css`
					background: linear-gradient(
						90deg,
						var(--bg-tertiary, #f0f0f0) 0%,
						var(--bg-secondary, #e8e8e8) 20%,
						var(--bg-tertiary, #f0f0f0) 40%,
						var(--bg-tertiary, #f0f0f0) 100%
					);
					background-size: 200% 100%;
					animation: ${shimmer} 1.5s ease-in-out infinite;
				`;
			case 'pulse':
				return css`
					animation: ${pulse} 1.5s ease-in-out infinite;
				`;
			default:
				return '';
		}
	}}
`;

export const Skeleton: FC<SkeletonBaseProps> = ({
	width,
	height,
	variant = 'rectangular',
	animation = 'shimmer',
	className,
	style,
}) => (
	<SkeletonBase
		$width={getWidth(width)}
		$height={getHeight(height, variant)}
		$variant={variant}
		$animation={animation}
		className={className}
		style={style}
		aria-hidden='true'
		role='presentation'
	/>
);

// SkeletonText - for text content
interface SkeletonTextProps extends Omit<SkeletonBaseProps, 'variant'> {
	lines?: number;
	lineHeight?: string | number;
	lastLineWidth?: string;
	gap?: string | number;
}

const SkeletonTextWrapper = styled.div<{ $gap: string }>`
	display: flex;
	flex-direction: column;
	gap: ${({ $gap }) => $gap};
	width: 100%;
`;

export const SkeletonText: FC<SkeletonTextProps> = ({
	lines = 3,
	width = '100%',
	height = '0.875rem',
	lineHeight,
	lastLineWidth = '75%',
	gap = '0.5rem',
	animation = 'shimmer',
	className,
	style,
}) => {
	const gapValue = typeof gap === 'number' ? `${gap}px` : gap;

	return (
		<SkeletonTextWrapper
			$gap={gapValue}
			className={className}
			style={style}
		>
			{Array.from({ length: lines }).map((_, index) => (
				<Skeleton
					key={index}
					width={index === lines - 1 ? lastLineWidth : width}
					height={lineHeight || height}
					variant='text'
					animation={animation}
				/>
			))}
		</SkeletonTextWrapper>
	);
};

// SkeletonAvatar - for profile pictures
interface SkeletonAvatarProps extends Omit<SkeletonBaseProps, 'variant'> {
	size?: 'small' | 'medium' | 'large' | number;
}

const avatarSizes = {
	small: 32,
	medium: 40,
	large: 56,
};

export const SkeletonAvatar: FC<SkeletonAvatarProps> = ({ size = 'medium', animation = 'shimmer', className, style }) => {
	const sizeValue = typeof size === 'number' ? size : avatarSizes[size];

	return (
		<Skeleton
			width={sizeValue}
			height={sizeValue}
			variant='circular'
			animation={animation}
			className={className}
			style={style}
		/>
	);
};

// SkeletonButton - for button placeholders
interface SkeletonButtonProps extends Omit<SkeletonBaseProps, 'variant'> {
	size?: 'small' | 'medium' | 'large';
}

const buttonSizes = {
	small: { width: 80, height: 32 },
	medium: { width: 100, height: 40 },
	large: { width: 120, height: 48 },
};

export const SkeletonButton: FC<SkeletonButtonProps> = ({
	size = 'medium',
	width,
	height,
	animation = 'shimmer',
	className,
	style,
}) => {
	const sizeConfig = buttonSizes[size];

	return (
		<Skeleton
			width={width || sizeConfig.width}
			height={height || sizeConfig.height}
			variant='rounded'
			animation={animation}
			className={className}
			style={style}
		/>
	);
};

// SkeletonCard - for card content
interface SkeletonCardProps {
	showImage?: boolean;
	imageHeight?: string | number;
	lines?: number;
	showActions?: boolean;
	animation?: SkeletonAnimation;
	className?: string;
	style?: CSSProperties;
}

const SkeletonCardWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: var(--spacing-4, 1rem);
	padding: var(--spacing-4, 1rem);
	background: var(--card-bg, #ffffff);
	border: 1px solid var(--card-border, #eeeeee);
	border-radius: var(--radius-lg, 8px);
`;

const SkeletonCardActions = styled.div`
	display: flex;
	gap: var(--spacing-2, 0.5rem);
	margin-top: auto;
`;

export const SkeletonCard: FC<SkeletonCardProps> = ({
	showImage = true,
	imageHeight = 160,
	lines = 3,
	showActions = true,
	animation = 'shimmer',
	className,
	style,
}) => (
	<SkeletonCardWrapper
		className={className}
		style={style}
	>
		{showImage && (
			<Skeleton
				height={imageHeight}
				variant='rounded'
				animation={animation}
			/>
		)}
		<SkeletonText
			lines={lines}
			animation={animation}
		/>
		{showActions && (
			<SkeletonCardActions>
				<SkeletonButton
					size='small'
					animation={animation}
				/>
				<SkeletonButton
					size='small'
					animation={animation}
				/>
			</SkeletonCardActions>
		)}
	</SkeletonCardWrapper>
);

// SkeletonTableRow - for table rows
interface SkeletonTableRowProps {
	columns?: number;
	showCheckbox?: boolean;
	showActions?: boolean;
	rowHeight?: string | number;
	animation?: SkeletonAnimation;
	className?: string;
}

const SkeletonTableRowWrapper = styled.tr`
	td {
		padding: 10px 15px;
		border-bottom: 1px solid var(--table-border, #f2f2f2);
	}
`;

const SkeletonTableCell = styled.td<{ $width?: string }>`
	width: ${({ $width }) => $width || 'auto'};
`;

export const SkeletonTableRow: FC<SkeletonTableRowProps> = ({
	columns = 5,
	showCheckbox = true,
	showActions = true,
	rowHeight = '1rem',
	animation = 'shimmer',
	className,
}) => {
	const columnWidths = ['15%', '20%', '15%', '20%', '15%'];

	return (
		<SkeletonTableRowWrapper className={className}>
			{showCheckbox && (
				<SkeletonTableCell $width='40px'>
					<Skeleton
						width={18}
						height={18}
						variant='rounded'
						animation={animation}
					/>
				</SkeletonTableCell>
			)}
			{Array.from({ length: columns }).map((_, index) => (
				<SkeletonTableCell
					key={index}
					$width={columnWidths[index % columnWidths.length]}
				>
					<Skeleton
						height={rowHeight}
						variant='text'
						animation={animation}
						width={`${60 + Math.random() * 40}%`}
					/>
				</SkeletonTableCell>
			))}
			{showActions && (
				<SkeletonTableCell $width='100px'>
					<div style={{ display: 'flex', gap: '8px' }}>
						<Skeleton
							width={24}
							height={24}
							variant='rounded'
							animation={animation}
						/>
						<Skeleton
							width={24}
							height={24}
							variant='rounded'
							animation={animation}
						/>
					</div>
				</SkeletonTableCell>
			)}
		</SkeletonTableRowWrapper>
	);
};

// SkeletonTable - full table skeleton
interface SkeletonTableProps {
	rows?: number;
	columns?: number;
	showHeader?: boolean;
	showCheckbox?: boolean;
	showActions?: boolean;
	animation?: SkeletonAnimation;
	className?: string;
	style?: CSSProperties;
}

const SkeletonTableWrapper = styled.div`
	width: 100%;
	overflow: auto;

	table {
		width: 100%;
		border-collapse: separate;
		border-spacing: 0 1px;
	}

	thead th {
		padding: 1rem 15px;
		border-bottom: 1px solid var(--table-border, #f2f2f2);
		background: var(--table-header-bg, #ffffff);
	}
`;

export const SkeletonTable: FC<SkeletonTableProps> = ({
	rows = 5,
	columns = 5,
	showHeader = true,
	showCheckbox = true,
	showActions = true,
	animation = 'shimmer',
	className,
	style,
}) => (
	<SkeletonTableWrapper
		className={className}
		style={style}
	>
		<table>
			{showHeader && (
				<thead>
					<tr>
						{showCheckbox && (
							<th style={{ width: '40px' }}>
								<Skeleton
									width={18}
									height={18}
									variant='rounded'
									animation={animation}
								/>
							</th>
						)}
						{Array.from({ length: columns }).map((_, index) => (
							<th key={index}>
								<Skeleton
									height='0.7rem'
									variant='text'
									animation={animation}
									width={`${50 + Math.random() * 30}%`}
								/>
							</th>
						))}
						{showActions && (
							<th style={{ width: '100px' }}>
								<Skeleton
									height='0.7rem'
									variant='text'
									animation={animation}
									width='60%'
								/>
							</th>
						)}
					</tr>
				</thead>
			)}
			<tbody>
				{Array.from({ length: rows }).map((_, index) => (
					<SkeletonTableRow
						key={index}
						columns={columns}
						showCheckbox={showCheckbox}
						showActions={showActions}
						animation={animation}
					/>
				))}
			</tbody>
		</table>
	</SkeletonTableWrapper>
);

// SkeletonForm - for form fields
interface SkeletonFormProps {
	fields?: number;
	showLabels?: boolean;
	animation?: SkeletonAnimation;
	className?: string;
	style?: CSSProperties;
}

const SkeletonFormWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: var(--spacing-5, 1.25rem);
`;

const SkeletonFormField = styled.div`
	display: flex;
	flex-direction: column;
	gap: var(--spacing-2, 0.5rem);
`;

export const SkeletonForm: FC<SkeletonFormProps> = ({
	fields = 4,
	showLabels = true,
	animation = 'shimmer',
	className,
	style,
}) => (
	<SkeletonFormWrapper
		className={className}
		style={style}
	>
		{Array.from({ length: fields }).map((_, index) => (
			<SkeletonFormField key={index}>
				{showLabels && (
					<Skeleton
						height='0.875rem'
						width={`${60 + Math.random() * 40}%`}
						variant='text'
						animation={animation}
					/>
				)}
				<Skeleton
					height='3.125rem'
					variant='rounded'
					animation={animation}
				/>
			</SkeletonFormField>
		))}
		<SkeletonButton
			size='large'
			width='100%'
			animation={animation}
		/>
	</SkeletonFormWrapper>
);

// SkeletonListItem - for list items
interface SkeletonListItemProps {
	showAvatar?: boolean;
	lines?: number;
	showSecondaryAction?: boolean;
	animation?: SkeletonAnimation;
	className?: string;
	style?: CSSProperties;
}

const SkeletonListItemWrapper = styled.div`
	display: flex;
	align-items: flex-start;
	gap: var(--spacing-3, 0.75rem);
	padding: var(--spacing-3, 0.75rem) 0;
	border-bottom: 1px solid var(--border-secondary, #eeeeee);
`;

const SkeletonListItemContent = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-1, 0.25rem);
`;

export const SkeletonListItem: FC<SkeletonListItemProps> = ({
	showAvatar = true,
	lines = 2,
	showSecondaryAction = false,
	animation = 'shimmer',
	className,
	style,
}) => (
	<SkeletonListItemWrapper
		className={className}
		style={style}
	>
		{showAvatar && (
			<SkeletonAvatar
				size='medium'
				animation={animation}
			/>
		)}
		<SkeletonListItemContent>
			<Skeleton
				height='1rem'
				width='60%'
				variant='text'
				animation={animation}
			/>
			{lines > 1 && (
				<Skeleton
					height='0.75rem'
					width='80%'
					variant='text'
					animation={animation}
				/>
			)}
			{lines > 2 && (
				<Skeleton
					height='0.75rem'
					width='40%'
					variant='text'
					animation={animation}
				/>
			)}
		</SkeletonListItemContent>
		{showSecondaryAction && (
			<Skeleton
				width={32}
				height={32}
				variant='rounded'
				animation={animation}
			/>
		)}
	</SkeletonListItemWrapper>
);

// SkeletonList - multiple list items
interface SkeletonListProps {
	items?: number;
	showAvatar?: boolean;
	lines?: number;
	animation?: SkeletonAnimation;
	className?: string;
	style?: CSSProperties;
}

export const SkeletonList: FC<SkeletonListProps> = ({
	items = 5,
	showAvatar = true,
	lines = 2,
	animation = 'shimmer',
	className,
	style,
}) => (
	<div
		className={className}
		style={style}
	>
		{Array.from({ length: items }).map((_, index) => (
			<SkeletonListItem
				key={index}
				showAvatar={showAvatar}
				lines={lines}
				animation={animation}
			/>
		))}
	</div>
);

// Export all components
export default Skeleton;
